import { createAsyncThunk } from '@reduxjs/toolkit';
import { DateNight, QueryParams } from '../../types';
import { Paginated, PaginationService } from '../../services/api-handlers/pagination';
import { DateNightApi } from '../../services/end-points';
import SortingService from '../../services/sorting';
import { resolveApiCall } from '../../services/api-handlers/api-resolver';
import { makeFormErrorsFromResponse } from '../../components/react-hook-form/utils/make-form-errors';
import { ThunkConfig } from '../configure-store';
import { DateNightFormData } from '../../screens/DateNight/components/DateNightForm';

export const listDateNights = createAsyncThunk<Paginated<DateNight>, QueryParams, ThunkConfig>(
    'date-night/list',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { dateNight } = getState();
        const { filters, sorting, ...rest } = payload;

        return PaginationService.resolveApiCall(
            thunkAPI,
            dateNight,
            async () =>
                DateNightApi.list({
                    ...filters,
                    ...SortingService.makeOrder(sorting),
                    ...rest,
                }),
            payload.page,
            dateNight.data,
        );
    },
);

export const getAllDateNights = createAsyncThunk<Paginated<DateNight>, undefined, ThunkConfig>(
    'date-night/all-list',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { dateNight } = getState();

        return PaginationService.resolveApiCall(
            thunkAPI,
            dateNight,
            async () =>
                DateNightApi.list({
                    limit: -1,
                }),
            1,
            {
                list: [],
                page: 1,
            },
        );
    },
);

export const getDateNight = createAsyncThunk<DateNight, { id: string; onError: (data: any) => any }, ThunkConfig>(
    'date-night/current',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { dateNight } = getState();
        const { id, onError } = payload;

        return resolveApiCall(thunkAPI, dateNight, async () => (await DateNightApi.get(id)).data, onError);
    },
);

export const deleteDateNight = createAsyncThunk<any, { id: string; callback: () => void }, ThunkConfig>(
    'date-night/delete',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { id } = payload;
        const { dateNight } = getState();
        await resolveApiCall(
            thunkAPI,
            dateNight,
            async () => (
                await DateNightApi.remove(id),
                async (err: { response: any }) => {
                    const { response } = err;
                    return makeFormErrorsFromResponse(response.data);
                }
            ),
        );
        payload.callback();
    },
);

export const createDateNight = createAsyncThunk<DateNight, DateNightFormData, ThunkConfig>(
    'date-night/create',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { dateNight } = getState();
        return resolveApiCall(
            thunkApi,
            dateNight,
            async () => await DateNightApi.create(payload),
            async (err) => {
                const { response } = err;
                return makeFormErrorsFromResponse(response.data);
            },
        );
    },
);

export const updateDateNight = createAsyncThunk<
    DateNight,
    { id: DateNight['id']; data: Partial<DateNightFormData> },
    ThunkConfig
>('date-night/update', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { dateNight } = getState();
    const { id, data } = payload;
    return resolveApiCall(
        thunkApi,
        dateNight,
        async () => await DateNightApi.update(id, data),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});
